import { Application } from '@platform/client';
import { Route, Switch, useLocation } from 'wouter';
import { ApplicationStorageDataKeys, Step } from '../Application';
import { ExperienceHopeSelection } from '../final-details/ExperienceHopeSelection';
import { IndividualSubmission } from '../final-details/individual/IndividualSubmission';
import { CurrentEmploymentSelection } from '../profile/individual/CurrentEmploymentSelection';
import { ExperienceDetailSelection } from '../profile/shared/ExperienceDetailSelection';
import { LocationSelection } from '../profile/shared/LocationSelection';
import { ProfilePictureUpload } from '../profile/shared/ProfilePictureUpload';
import { ResumeSelection } from '../profile/shared/ResumeSelection';
import { ImpactAreaSelection } from '../program/shared/ImpactAreaSelection';
import { IndividualApplicationApproved } from '../result/individual/IndividualApproved';
import { SkillsImpactSelection } from '../skills/SkillsImpactSelection';
import { SkillsExperience } from '../skills/SkillsSelection';

enum IndividualApplicationSteps {
  IMPACT_AREA_SELECTION = 'IMPACT_AREA_SELECTION',

  CURRENT_EMPLOYMENT_SELECTION = 'CURRENT_EMPLOYMENT_SELECTION',
  RESUME_SELECTION = 'RESUME_SELECTION',
  LOCATION_SELECTION = 'LOCATION_SELECTION',
  EXPERIENCE_SELECTION = 'EXPERIENCE_SELECTION',
  PHOTO_SELECTION = 'PHOTO_SELECTION',

  SKILLS_SELECTION = 'SKILLS_SELECTION',
  SKILL_IMPACT_SELECTION = 'SKILL_IMPACT_SELECTION',

  EXPERIENCE_HOPE_SELECTION = 'EXPERIENCE_HOPE_SELECTION',
  REFERRAL_SELECTION = 'REFERRAL_SELECTION',
}

const IndividualApplicationStep: Record<IndividualApplicationSteps, Step> = {
  [IndividualApplicationSteps.IMPACT_AREA_SELECTION]: {
    progress: 80,
    component: ImpactAreaSelection,
    url: '/',
  },
  [IndividualApplicationSteps.CURRENT_EMPLOYMENT_SELECTION]: {
    progress: 135,
    component: CurrentEmploymentSelection,
    url: '/current-employment-selection',
  },
  [IndividualApplicationSteps.RESUME_SELECTION]: {
    progress: 150,
    component: ResumeSelection,
    url: '/resume-selection',
  },
  [IndividualApplicationSteps.LOCATION_SELECTION]: {
    progress: 135,
    component: LocationSelection,
    url: '/location-selection',
  },
  [IndividualApplicationSteps.EXPERIENCE_SELECTION]: {
    progress: 170,
    component: ExperienceDetailSelection,
    url: '/experience-selection',
  },
  [IndividualApplicationSteps.PHOTO_SELECTION]: {
    progress: 190,
    component: ProfilePictureUpload,
    url: '/photo-selection',
  },
  [IndividualApplicationSteps.SKILLS_SELECTION]: {
    progress: 235,
    component: SkillsExperience,
    url: '/skills-selection',
  },
  [IndividualApplicationSteps.SKILL_IMPACT_SELECTION]: {
    progress: 275,
    component: SkillsImpactSelection,
    url: '/skill-impact-selection',
  },
  [IndividualApplicationSteps.EXPERIENCE_HOPE_SELECTION]: {
    progress: 340,
    component: ExperienceHopeSelection,
    url: '/experience-hope-selection',
  },
  [IndividualApplicationSteps.REFERRAL_SELECTION]: {
    progress: 385,
    component: IndividualSubmission,
    url: '/referral-selection',
  },
} as const;

export const IndividualApplicationRoot = () => {
  const [location, navigate] = useLocation();

  const goToNextStep = () => {
    const steps = Object.values(IndividualApplicationSteps);
    const currentIndex = steps.findIndex((step) => {
      return IndividualApplicationStep[step as IndividualApplicationSteps].url === location;
    });
    const nextStep =
      currentIndex < steps.length - 1 ? steps[currentIndex + 1] : steps[currentIndex];
    const nextUrl = IndividualApplicationStep[nextStep as IndividualApplicationSteps].url;
    navigate(nextUrl);
    return nextStep as IndividualApplicationSteps;
  };

  const goToPreviousStep = () => {
    const steps = Object.values(IndividualApplicationSteps);
    const currentIndex = steps.findIndex(
      (step) => IndividualApplicationStep[step as IndividualApplicationSteps].url === location,
    );

    if (currentIndex === 0) {
      window.location.href = 'platform/auth/application-start';
      return;
    }

    const previousStep = steps[currentIndex - 1];
    const previousUrl = IndividualApplicationStep[previousStep as IndividualApplicationSteps].url;
    navigate(previousUrl);
    return previousStep as IndividualApplicationSteps;
  };

  const applicationData = Application.useGetProfessionalApplicationStatus();

  if (applicationData.data?.applyCompleted) {
    return <IndividualApplicationApproved />;
  }

  return (
    <Switch>
      {Object.entries(IndividualApplicationStep).map(([_key, value]) => {
        const { url, component: Component, progress } = value;

        return (
          <Route key={url} path={url}>
            <Component
              onBack={goToPreviousStep}
              onContinue={goToNextStep}
              progress={progress}
              dataKey={ApplicationStorageDataKeys.PROFESSIONAL_APPLICATION}
            />
          </Route>
        );
      })}
    </Switch>
  );
};
